import { Button, Card, Typography } from "@norican/react";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";

import { Microsoft } from "@/components/icons/microsoft";
import { NoricanLogo } from "@/components/norican-logo";
import { PRODUCT_EXPANSION, PRODUCT_NAME, PRODUCT_TAGLINE } from "@/lib/brand";
import { logger } from "@/lib/logger";

// Local dev can't complete a real Microsoft sign-in (Entra won't redirect back
// to localhost), so the dev server shows a login picker that hits the
// backend's `/auth/dev-login` instead — a genuine session, just without
// Microsoft. Production builds always show the Microsoft button; the backend
// refuses `/auth/dev-login` there regardless (see DEV_AUTH_BYPASS).
const SHOW_DEV_LOGIN = import.meta.env.DEV;

export function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState("");
  const [devSigningIn, setDevSigningIn] = useState(false);

  const handleMicrosoftLogin = () => {
    logger.debug("[login] redirecting to Microsoft SSO");
    window.location.assign("/api/auth/sso/login");
  };

  const handleDevLogin = async (role: "user" | "admin") => {
    logger.debug("[login] dev sign-in as", role);
    setError("");
    setDevSigningIn(true);
    try {
      const response = await fetch(`/api/auth/dev-login?role=${role}`, { method: "POST" });
      if (!response.ok) {
        throw new Error(`dev-login responded ${response.status}`);
      }
      logger.debug("[login] dev sign-in succeeded → redirecting to /overview");
      navigate("/overview");
    } catch (err) {
      logger.debug("[login] dev sign-in failed:", err);
      setError("Local sign-in failed. Please try again.");
      setDevSigningIn(false);
    }
  };

  useEffect(() => {
    const ssoError = searchParams.get("error");
    if (!ssoError) {
      return;
    }
    logger.debug("[login] sso error:", ssoError);
    setError(
      ssoError === "domain_not_allowed"
        ? "Your account isn't allowed to sign in to this app."
        : "Sign-in failed. Please try again.",
    );
  }, [searchParams]);

  return (
    <div className="bg-bg flex min-h-screen items-center justify-center px-4">
      <Card className="w-full max-w-md">
        <Card.Header>
          {/* Card.Header lays its children out in a row, so the identity block
              needs its own column wrapper — without it the mark, the name and
              both straplines string out side by side. */}
          <div className="flex w-full flex-col items-center gap-1 text-center">
            {/* Larger here than in the navbar: this is the first screen anyone
                sees, and it is the company that authorises the sign-in. */}
            <NoricanLogo height={34} className="mb-3" />
            <Typography as="h1" variant="heading-2">
              {PRODUCT_NAME}
            </Typography>
            <p className="text-fg-muted text-sm">{PRODUCT_EXPANSION}</p>
            <p className="text-fg-subtle text-xs">{PRODUCT_TAGLINE}</p>
          </div>
        </Card.Header>
        <Card.Body>
          {SHOW_DEV_LOGIN ? (
            <div className="space-y-2">
              <p className="text-fg-muted text-center text-xs">
                Local dev — Microsoft sign-in only works on the deployed app.
              </p>
              <Button
                type="button"
                disabled={devSigningIn}
                onClick={() => handleDevLogin("user")}
                fullWidth
              >
                Continue as User
              </Button>
              <Button
                type="button"
                disabled={devSigningIn}
                variant="outline"
                onClick={() => handleDevLogin("admin")}
                fullWidth
              >
                Continue as Admin
              </Button>
            </div>
          ) : (
            <Button
              type="button"
              onClick={handleMicrosoftLogin}
              startIcon={<Microsoft className="size-4" />}
              fullWidth
            >
              Sign in with Microsoft
            </Button>
          )}
          {error && <p className="text-danger-fg mt-4 text-center text-sm">{error}</p>}
        </Card.Body>
      </Card>
    </div>
  );
}
